// Per-branch lift wrapper for a DRIVEN frame: hold the decoder outputs, cull what breaks.
// Notes: ~/comments/aos-threejs-poc/drivenLifter_createDrivenLifters_two_things_the_renderer_lacks.md

import { sanitizeDrivenGaussians, sumCulled, type DrivenGaussians } from "./drivenGaussians.ts";
import type { PbrShadeLike } from "../inference/pbrShadeParams.ts";

/** The slice of a branch this needs — its own lifter, by name. */
export interface LiftSource {
  name: string;
  lifter: {
    lift(verts: Float32Array, geomUv: unknown, colorUv: unknown, options?: { skipUvUpload?: boolean }):
      DrivenGaussians | Promise<DrivenGaussians>;
    /** Packed mode: encode + submit, no readback. The renderer calls THIS on the
     *  packed path and never `lift`, which throws there. */
    submitLift?(geomUv: unknown, colorUv: unknown, options?: { skipUvUpload?: boolean; skipReadback?: boolean }): void;
    readonly packedMode?: boolean;
    readonly pbrShade?: PbrShadeLike | null;
  } | null;
}

/** What the wrapper hands the renderer: the driven lift, and every seam the
 *  renderer reaches for on the lifter it was given. */
export interface WrappedLifter {
  lift(verts: Float32Array, geomUv: unknown, colorUv: unknown): Promise<DrivenGaussians>;
  /** Forwarded so the PACKED path works through the wrapper. Absent on a lifter
   *  that has none, which is what makes `OGSRenderer` treat the set as SoA. */
  submitLift?(geomUv: unknown, colorUv: unknown, options?: { skipUvUpload?: boolean; skipReadback?: boolean }): void;
  readonly packedMode?: boolean;
  readonly pbrShade: PbrShadeLike | null;
}

export interface DrivenLifters {
  /** The lifter to hand the renderer for `source`, wrapping that branch's own. */
  lifterFor(source: LiftSource, index: number): WrappedLifter;
  /** Splats culled across every branch on the last pass. */
  readonly culled: number;
  /** Per branch, for a report that names which region broke. */
  culledBy(name: string): number;
  /** Only a DRIVEN frame culls: the neutral pose's vertices are the ones the decoders were fit to. */
  setDriving(driving: boolean): void;
}

/**
 * Two things the renderer's own source option cannot express:
 *
 *  (a) `skipUvUpload` — a driven frame re-lifts the SAME frozen decoder outputs at new
 *      vertices, so re-uploading identical UVs every frame is pure waste;
 *  (b) the over-stretch cull, which has to run on the SoA fields BEFORE the concat.
 */
export function createDrivenLifters(sources: readonly LiftSource[], maxScale?: number): DrivenLifters {
  const cull = new Array(sources.length).fill(0);
  const indexByName = new Map(sources.map((source, index) => [source.name, index]));
  let driving = false;
  return {
    get culled() { return sumCulled(cull); },
    culledBy: (name) => cull[indexByName.get(name) ?? -1] ?? 0,
    setDriving(next) { driving = !!next; },
    lifterFor(source, index) {
      let lastGeom: unknown = null;
      let lastColor: unknown = null;

      /** True when this call re-lifts the SAME frozen decoder outputs — the one
       *  saving this wrapper still exists for. A driven frame re-lifts at new
       *  VERTICES, so re-uploading identical UVs every pose is pure waste. */
      const sameUvs = (geomUv: unknown, colorUv: unknown): boolean => {
        const same = lastGeom === geomUv && lastColor === colorUv;
        lastGeom = geomUv;
        lastColor = colorUv;
        return same;
      };

      const own: WrappedLifter = {
        // NORMALISED, not forwarded. The proxy below would hand back `undefined`
        // for a branch whose lifter has no shade stage, and `undefined` is the
        // shape a MISSING member has — an RGB branch genuinely having none is a
        // fact, and the renderer should be told it rather than left to infer it.
        get pbrShade() { return source.lifter?.pbrShade ?? null; },
        get packedMode() { return !!source.lifter?.packedMode; },
        // THE PACKED PATH: encode + submit, no readback, nothing crossing to the
        // CPU. The over-stretch cull is NOT here — it moved into
        // `lift_pass2.wgsl`, because running it on the CPU is what forced this
        // path to read every splat back each pose (measured on myra-pbr: 212 ms
        // of a 276 ms pose, the avatar updating 3.6x/s while the page drew 20 fps).
        submitLift(geomUv: unknown, colorUv: unknown, options: { skipReadback?: boolean } = {}) {
          const lifter = source.lifter;
          if (!lifter?.submitLift) {
            throw new Error(`[ogs] branch '${source.name}' has no GpuLifter.submitLift — `
              + `its lift is ${lifter ? "not the GPU one (CPU GaussianLifter?)" : "not built yet"}, `
              + "so it cannot present packed");
          }
          lifter.submitLift(geomUv, colorUv, { ...options, skipUvUpload: sameUvs(geomUv, colorUv) });
          // Nothing to count: a culled texel is one the shader RETURNED on, and its
          // packed slot was pre-cleared to transparent. -1 is "not counted on this
          // path" — 0 would read as a clean frame, which is the silent-fallback shape.
          cull[index] = -1;
        },
        async lift(verts: Float32Array, geomUv: unknown, colorUv: unknown) {
          const lifter = source.lifter;
          if (!lifter) throw new Error(`[ogs] branch '${source.name}' has no lifter to drive`);
          const gaussians = await lifter.lift(verts, geomUv, colorUv, { skipUvUpload: sameUvs(geomUv, colorUv) });
          // The SoA path still culls on the CPU: it has the arrays in hand and has
          // already paid the readback, so there is nothing to save by deferring it.
          cull[index] = driving ? sanitizeDrivenGaussians(gaussians, maxScale) : 0;
          return gaussians;
        },
      };

      // TRANSPARENT EXCEPT FOR THE TWO THINGS IT ADDS.
      //
      // This used to forward a hand-written list of members, and the list was
      // always going to be short: `OGSRenderer` reads `gpuDevice`,
      // `packedByteLength`, `setThreeTargets`, `setSharedReadback` and
      // `finishLift` off the lifter it was handed, and a wrapper naming only
      // `lift` and `pbrShade` hid every one of them — the renderer then refused
      // the whole set with "packed source 'head' needs a GpuLifter (submitLift)",
      // which is true of the WRAPPER and false of the branch. Forwarding
      // everything makes the wrapper's surface the lifter's surface by
      // construction, so adding a member to GpuLifter cannot silently fail here.
      //
      // Resolved per ACCESS, not captured: `attachPbr` can replace the shade
      // stage, and `branch.lifter` itself can be built after the wrapper is.
      // `Reflect`, not an index cast: reaching a dynamic key on a typed object is
      // exactly what it is for, and it keeps this module free of the double-cast
      // escape hatch that `ogsTypeDebt` permits only in rigMath.ts. (That test
      // greps the SOURCE, so spelling the hatch out even in a comment trips it —
      // which is the check working, not a false positive.)
      return new Proxy(own, {
        get(target, prop, receiver) {
          if (prop in target) return Reflect.get(target, prop, receiver);
          const lifter = source.lifter;
          if (!lifter) return undefined;
          const value = Reflect.get(lifter, prop);
          // Bound: a method pulled off the lifter and called on the proxy would
          // otherwise run with `this` as the proxy and touch none of its fields.
          return typeof value === "function" ? value.bind(lifter) : value;
        },
        has(target, prop) {
          if (prop in target) return true;
          const lifter = source.lifter;
          return !!lifter && Reflect.has(lifter, prop);
        },
      });
    },
  };
}
